import type {AttributeMetaData, WritebackDataType} from 'src/types/writeback';
import type {EntityType} from 'src/types/ats-entities';
import type {
  EditableNode,
  ResponseType,
  DerivedFieldMap,
  AnswerId,
} from 'src/types/chatbot';
import type {DynamicLabels} from 'src/types/dynamic-labels';
import {selectEntityTypeMappingsByName} from 'src/selectors/ats-entities';

import * as React from 'react';
import invariant from 'invariant';
import produce from 'immer';

import {useSelector} from 'react-redux';
import {CompositeDecorator} from 'draft-js';
// @ts-expect-error - TS7016 - Could not find a declaration file for module 'draft-js/lib/DraftDecorator'. '/Users/diwaker/SpacedOut/ui-server/node_modules/draft-js/lib/DraftDecorator.js' implicitly has an 'any' type.
import type {DraftDecorator} from 'draft-js/lib/DraftDecorator';

import {isBranchableNodeType} from '../utils';

import nanoid from 'src/utils/id';
import {createDynamicLabelSelector} from 'src/selectors/dynamic-labels';
import {dynamicLabelDecorator} from 'src/components/lib/markdown-editor/dynamic-text';
import {useChatbotStateContext} from '../chatbot-state-context';


const selectDynamicLabels = createDynamicLabelSelector({depth: 'rte'});
export function useAtsVariables(entityType: EntityType): DynamicLabels {
  return useSelector((state) => selectDynamicLabels(state, entityType));
}

export function useChatbotURLParams(
  url_params_list: Array<{
    [urlParamName: string]: string;
  }> = [],
): DynamicLabels {
  const urlParamsMap = url_params_list.reduce<Record<string, any>>(
    (acc, curr) => {
      Object.entries(curr).forEach(([key, value]: [any, any]) => {
        acc[key] = value;
      });
      return acc;
    },
    {},
  );

  // every item has {key: value} structure where key and value are the same
  const urlParamsMapped = Object.keys(urlParamsMap).map((key) => ({
    type: 'string',
    source: 'custom',
    name: key,
    label: key,
    value: key,
    rteValue: `<${key}>`,
    template: '<$>',
    display_entity_type: 'Custom URL Parameters',
    related_entities: [],
    category: 'Custom URL Parameters',
    description: `${key} from Custom URL Parameters`,
    resolvedId: key,
    mapping: {display_name: 'Custom URL Parameters'},
  }));
  return urlParamsMapped;
}

export function useChatbotVariables(
  entityType: EntityType,
  config: {
    showDiscoverJobId?: boolean;
    showDiscoverAttributes?: boolean;
    showExternalVariables?: boolean;
  } = {
    showDiscoverJobId: false,
    showDiscoverAttributes: false,
    showExternalVariables: false,
  },
  url_params_list: Array<{
    [urlParamName: string]: string;
  }> = [],
): DynamicLabels {
  const dynamicLabels = useAtsVariables(entityType);
  const externalVars = useSelector((state) => state.chatbot.externalVariables);
  const discoverJobAttributes = useSelector(
    (state) => state.chatbot.discoverJobAttributes,
  );
  const atsMappings = useSelector(selectEntityTypeMappingsByName);

  const externalVariablesMapped =
    externalVars
      // @ts-expect-error - TS7031 - Binding element 'entity' implicitly has an 'any' type.
      ?.filter(({entity}) => entity === entityType)
      // @ts-expect-error - TS7031 - Binding element 'parameter' implicitly has an 'any' type. | TS7031 - Binding element 'data_type' implicitly has an 'any' type. | TS7031 - Binding element 'entity' implicitly has an 'any' type.
      .map(({parameter, data_type, entity}) => ({
        type: data_type,
        source: 'custom',
        name: parameter,
        label: parameter,
        fieldLabel: parameter,
        value: `external/${parameter}`,
        rteValue: `external/<${parameter}>`,
        template: '<$>',
        display_entity_type: `external/${entity}`,
        related_entities: [],
        category: 'API Bot',
        description: '',
        resolvedId: `external/${parameter}`,
        mapping: {display_name: 'API Bot'},
        entityRelationshipPath: [],
      })) ?? [];

  const discoverJobAttributesMapped =
    // @ts-expect-error - TS7031 - Binding element 'data_type' implicitly has an 'any' type. | TS7031 - Binding element 'label' implicitly has an 'any' type. | TS7031 - Binding element 'value' implicitly has an 'any' type.
    discoverJobAttributes?.map(({data_type, label, value}) => ({
      type: data_type,
      source: 'custom',
      name: label,
      label,
      value,
      rteValue: `<${value}>`,
      template: '<$>',
      display_entity_type: 'Discover Job Attribute',
      related_entities: [],
      category: 'Discover Job Match',
      description: `${label} of the selected discover job`,
      resolvedId: value,
      mapping: {display_name: 'Discover Job Match'},
      entityRelationshipPath: [],
      dynamicLabelType: 'discover_attribute',
    })) ?? [];

  const chatbotState = useChatbotStateContext()?.state;
  const currentGraph = chatbotState
    ? chatbotState.history[chatbotState.historyIndex]
    : null;

  // flag to see if anchor entity is submittal
  const usesApplicationVariables = currentGraph
    ? atsMappings[currentGraph.anchor_entity_type]?.sense_name ===
      'job_submission'
    : false;

  // TODO: (Aman) Remove this if discover job id becomes a part of discover job attributes
  const discoverJobIdVariable = {
    type: 'string',
    source: 'custom',
    name: 'Discover Job ID',
    label: 'Discover Job ID',
    value: 'selected_job/external_job_id',
    rteValue: 'selected_job/external_job_id',
    template: '<$>',
    display_entity_type: 'Application Bot',
    related_entities: [],
    category: 'Application Bot',
    description: 'The job id from discover',
    resolvedId: 'selected_job/external_job_id',
    mapping: {display_name: 'Application Bot Externals'},
  } as const;

  const urlParamsMap = url_params_list.reduce<Record<string, any>>(
    (acc, curr) => {
      Object.entries(curr).forEach(([key, value]: [any, any]) => {
        acc[key] = value;
      });
      return acc;
    },
    {},
  );

  // url_params_map has {key: value} structure where key and value are the same
  const urlParamsMapped = Object.keys(urlParamsMap).map((key) => ({
    type: 'string',
    source: 'custom',
    name: key,
    label: key,
    value: key,
    rteValue: `<${key}>`,
    template: '<$>',
    display_entity_type: 'Custom URL Parameters',
    related_entities: [],
    category: 'Custom URL Parameters',
    description: `${key} from Custom URL Parameters`,
    resolvedId: key,
    mapping: {display_name: 'Custom URL Parameters'},
  }));

  const applicationBotVariables = [
    {
      type: 'string',
      source: 'custom',
      name: 'Job ID',
      label: 'external/job_id',
      fieldLabel: 'Job ID',
      value: 'external/job_id',
      rteValue: '<external/job_id>',
      template: '<$>',
      display_entity_type: 'Application Bot',
      related_entities: [],
      category: 'Application Bot',
      description: 'The job id from URL',
      resolvedId: 'external/job_id',
      mapping: {display_name: 'Application Bot Externals'},
    },
    {
      type: 'string',
      source: 'custom',
      name: 'Job Title',
      label: 'external/job_title',
      fieldLabel: 'Job Title',
      value: 'external/job_title',
      rteValue: '<external/job_title>',
      template: '<$>',
      display_entity_type: 'Application Bot',
      related_entities: [],
      category: 'Application Bot',
      description: 'The supplied job title',
      resolvedId: 'external/job_title',
      mapping: {display_name: 'Application Bot Externals'},
    },
    {
      type: 'string',
      source: 'custom',
      name: 'Job Category',
      label: 'external/job_category',
      fieldLabel: 'Job Category',
      value: 'external/job_category',
      rteValue: '<external/job_category>',
      template: '<$>',
      display_entity_type: 'Application Bot',
      related_entities: [],
      category: 'Application Bot',
      description: 'The supplied job category',
      resolvedId: 'external/job_category',
      mapping: {display_name: 'Application Bot Externals'},
    },
  ];

  const options = React.useMemo(
    () => [
      {
        source: 'custom',
        name: 'Estimated Time',
        label: 'chatbot/estimated_minutes',
        fieldLabel: 'Estimated Time',
        value: 'chatbot/estimated_minutes',
        rteValue: '{{chatbot/estimated_minutes}}',
        template: '{{$}}',
        display_entity_type: 'Chatbot',
        related_entities: [],
        category: 'Chatbot',
        resolvedId: 'chatbot/estimated_minutes',
        description: 'the estimated minutes in the conversation',
        mapping: {display_name: 'Chatbot'},
      },
      ...(usesApplicationVariables
        ? config.showDiscoverJobId
          ? [discoverJobIdVariable, ...applicationBotVariables]
          : applicationBotVariables
        : []),
      ...(config.showExternalVariables ? externalVariablesMapped : []),
      ...dynamicLabels,
      ...(usesApplicationVariables && config.showDiscoverAttributes
        ? discoverJobAttributesMapped
        : []),
      ...urlParamsMapped,
    ],
    [dynamicLabels, usesApplicationVariables],
  );
  return options;
}

export function useDraftDecorator(
  dynamicLabels: DynamicLabels,
  extraDecorators: Array<DraftDecorator> = [],
): CompositeDecorator {
  return React.useMemo(
    () =>
      new CompositeDecorator([
        dynamicLabelDecorator(dynamicLabels),
        ...extraDecorators,
      ]),
    [dynamicLabels],
  );
}

export function useEditorState<N extends EditableNode>({
  defaultNode,
}: {
  defaultNode: N;
}): [ReducerState<N>, QuestionDispatch<N>] {
  return React.useReducer(reducer, defaultNode, (defaultNode) => ({
    node: defaultNode,
  }));
}

export function finalizeReducerState<N extends EditableNode>(
  state: ReducerState<N>,
  writebackMetadata?: AttributeMetaData | null,
): N {
  return state.node;
}

type UpdateAction<T> = {
  type: 'update';
  payload: Partial<T>;
};

type SetWritebackAction = {
  type: 'set_writeback';
  payload: string | null | undefined;
  metadata: {
    previous: AttributeMetaData | null | undefined;
    next: AttributeMetaData | null | undefined;
  };
};

type SetAddressValidationAction = {
  type: 'set_address_writeback';
  payload: string; // the .name of the derived field;
  metadata: {
    writebackFieldId: string | null | undefined; // a wbfid, if any;
  };
};

type ChangeButtonsTypeAction = {
  type: 'change_buttons_type';
  payload: null | 'buttons' | 'boolean' | 'string' | 'multiselect';
};

type AddButtonTypeAction = {
  type: 'add_button';
};

type RemoveButtonTypeAction = {
  type: 'remove_button';
  payload: AnswerId;
};

type Action<T> =
  | UpdateAction<T>
  | SetWritebackAction
  | ChangeButtonsTypeAction
  | SetAddressValidationAction
  | AddButtonTypeAction
  | RemoveButtonTypeAction;
export type QuestionDispatch<T> = (arg1: Action<T>) => void;
export type ReducerState<T> = {
  node: T;
};

const generate_address_derived_fields = (): DerivedFieldMap => {
  const fieldMap: Record<string, any> = {};
  for (const [field_name, field_label] of [
    ['address_l1', 'Address Line 1'],
    ['address_l2', 'Address Line 2'],
    ['city', 'City'],
    ['state', 'State'],
    ['zip', 'Zip Code'],
  ]) {
    fieldMap[field_name] = {
      field_name,
      field_label,
      editable: true,
      exported: true,
      writeback_field_id: null,
    };
  }
  return fieldMap;
};

function reducer<T extends EditableNode>(
  state: ReducerState<T>,
  action: Action<T>,
): ReducerState<T> {
  switch (action.type) {
    case 'update': {
      const {node} = state;
      let newAttrs = action.payload;
      //the purpose of newAttrs is so that we could do proper spreading and guarantee that the changes to node attrs in this block will be reflected and not overwritten by attrs included in action.payload
      // @ts-expect-error - TS2339 - Property 'response_type' does not exist on type 'Partial<T>'.
      if (node.type === 'question' && action.payload.response_type) {
        // @ts-expect-error - TS2339 - Property 'response_type' does not exist on type 'Partial<T>'.
        const {response_type} = action.payload;
        // TODO[Aman] : fix below error introduced in 0.177.0
        if (response_type === 'enum' && !node.answers) {
          newAttrs = {
            ...newAttrs,
            answers: [
              {
                id: nanoid(),
                value: '',
                label: '',
              },
            ],
          };
        } else if (
          response_type === 'address' &&
          // @ts-expect-error - TS2339 - Property 'preset_question_id' does not exist on type 'Partial<T>'.
          !newAttrs.preset_question_id
        ) {
          // until ui changes, clear out any set writeback field when setting up address validation
          // TODO(marcos): clear out any unneeded field_maps during preflight since changing validation
          // type will retain stale field maps
          newAttrs = {
            ...newAttrs,
            derived_field_map:
              node.derived_field_map ?? generate_address_derived_fields(),
            answers: null,
            question_is_multiselect: false,
            question_error_message:
              // @ts-expect-error - TS2339 - Property 'question_error_message' does not exist on type 'Partial<T>'.
              newAttrs?.question_error_message ?? addressErrorMessage,
          };
        }
      }
      return {...state, node: {...node, ...newAttrs}};
      //newAttrs here includes action.payload and the attributes added in the reducer as well
    }

    case 'set_writeback': {
      let {node} = state;
      const {next, previous} = action.metadata;
      const possible_values = next?.possible_answers || next?.possible_values;

      if (node.type === 'question') {
        if (possible_values && !node.preset_question_id) {
          node = {
            ...node,
            answers: possible_values.map((possibleVal) => ({
              id: nanoid(),
              value:
                typeof possibleVal === 'string'
                  ? possibleVal
                  : possibleVal.value,
              label:
                typeof possibleVal === 'string'
                  ? possibleVal
                  : possibleVal.label,
              disabled: false,
            })),
          };
        } else if (
          !node.preset_question_id &&
          (previous?.possible_values || previous?.possible_answers)
        ) {
          node = {
            ...node,
            answers: null,
          };
        }

        if (next?.allow_select_multiple) {
          node = {
            ...node,
            question_is_multiselect: true,
          };
        } else if (previous?.allow_select_multiple || possible_values) {
          node = {
            ...node,
            question_is_multiselect: false,
          };
        }

        if (next?.data_type) {
          // @ts-expect-error - TS2339 - Property 'preset_question_id' does not exist on type 'EditableNode'.
          const response_type = node.preset_question_id
            ? // @ts-expect-error - TS2339 - Property 'response_type' does not exist on type 'EditableNode'.
              node.response_type
            : writebackDataTypeToResponseType[next.data_type] || next.data_type;

          let answers;
          if (response_type === 'boolean') {
            answers = booleanAnswers;
          } else {
            // @ts-expect-error - TS2339 - Property 'response_type' does not exist on type 'EditableNode'.
            if (node.response_type === 'boolean') {
              answers = null;
            } else {
              // @ts-expect-error - TS2339 - Property 'answers' does not exist on type 'EditableNode'.
              answers = node.answers;
            }
          }

          node = {
            ...node,
            response_type,
            answers,
          };
        } else if (previous?.data_type) {
          node = {
            ...node,
            // @ts-expect-error - TS2339 - Property 'preset_question_id' does not exist on type 'EditableNode'.
            response_type: node.preset_question_id
              ? // @ts-expect-error - TS2339 - Property 'response_type' does not exist on type 'EditableNode'.
                node.response_type
              : 'string',
            // @ts-expect-error - TS2339 - Property 'answers' does not exist on type 'EditableNode'.
            answers: previous.data_type === 'boolean' ? null : node.answers,
          };
        }

        return {...state, node: {...node, writeback_field_id: action.payload}};
      } else if (node.type === 'disposition') {
        const nextDataType = next?.data_type;
        if (
          possible_values ||
          (nextDataType &&
            nextDataType !== 'string' &&
            nextDataType !== previous?.data_type)
        ) {
          node = {
            ...node,
            disposition_writeback_value: null,
          };
        } else if (next?.possible_values || next?.data_type !== 'string') {
          node = {
            ...node,
            text_draft_json: null,
          };
        }

        return {...state, node: {...node, writeback_field_id: action.payload}};
      } else if (node.type === 'file_upload') {
        return {...state, node: {...node, writeback_field_id: action.payload}};
      } else {
        throw new Error(
          'Can only set writeback on a question, file upload or disposition node.',
        );
      }
    }

    case 'set_address_writeback': {
      const {node} = state;
      if (node.type === 'question') {
        let {derived_field_map} = node;
        if (derived_field_map == null) {
          throw new Error(
            'Cannot assign writeback field ids if a derived field map is not set',
          );
        }
        derived_field_map = {
          ...derived_field_map,
          [action.payload]: {
            ...derived_field_map[action.payload],
            writeback_field_id: action.metadata.writebackFieldId,
          },
        };
        return {...state, node: {...node, derived_field_map}};
      } else {
        throw new Error(
          'Can only set writeback field mappings for address nodes',
        );
      }
    }

    case 'change_buttons_type': {
      let {node} = state;
      invariant(
        node.type === 'question',
        'Only questions can have button types.',
      );

      const customButtonsTypes = new Set(['buttons', 'multiselect']);

      const buttonsType = action.payload;
      if (buttonsType === 'boolean') {
        node = {
          ...node,
          response_type: 'boolean',
          answers: booleanAnswers,
          question_is_multiselect: false,
          question_error_message: multipleChoiceErrorMessage,
        };
        // @ts-expect-error - TS2345 - Argument of type 'string | null' is not assignable to parameter of type 'string'.
      } else if (customButtonsTypes.has(buttonsType)) {
        node = {
          ...node,
          response_type:
            node.response_type === 'boolean' ? 'string' : node.response_type,
          answers: node.response_type === 'boolean' ? [] : node.answers ?? [],
          question_is_multiselect: buttonsType === 'multiselect',
          question_error_message: multipleChoiceErrorMessage,
        };
      } else {
        node = {
          ...node,
          response_type:
            node.response_type === 'boolean' ? 'string' : node.response_type,
          answers: null,
          question_is_multiselect: false,
          question_error_message: stringErrorMessage,
        };
      }
      return {...state, node};
    }
    case 'add_button': {
      if (isBranchableNodeType(state.node)) {
        const nextState = produce(state, (draft) => {
          // @ts-expect-error - TS2339 - Property 'answers' does not exist on type 'EditableNode | WritableDraft<object & BaseNode & { type: "condition"; condition_ruleset: LogicalAndExpression; }> | ... 12 more ... | WritableDraft<...>'.
          if (draft.node.answers != null) {
            // @ts-expect-error - TS2339 - Property 'answers' does not exist on type 'EditableNode | WritableDraft<object & BaseNode & { type: "condition"; condition_ruleset: LogicalAndExpression; }> | ... 12 more ... | WritableDraft<...>'.
            draft.node.answers.push({id: nanoid(), value: '', label: ''});
          }
        });
        return nextState;
      }
      return state; // noop
    }
    case 'remove_button': {
      const nextState = produce(state, (draft) => {
        if (isBranchableNodeType(draft.node)) {
          // @ts-expect-error - TS2339 - Property 'answers' does not exist on type 'EditableNode | WritableDraft<object & BaseNode & { type: "condition"; condition_ruleset: LogicalAndExpression; }> | ... 12 more ... | WritableDraft<...>'.
          draft.node.answers =
            // @ts-expect-error - TS2339 - Property 'answers' does not exist on type 'EditableNode | WritableDraft<object & BaseNode & { type: "condition"; condition_ruleset: LogicalAndExpression; }> | ... 12 more ... | WritableDraft<...>'. | TS2339 - Property 'answers' does not exist on type 'EditableNode | WritableDraft<object & BaseNode & { type: "condition"; condition_ruleset: LogicalAndExpression; }> | ... 12 more ... | WritableDraft<...>'.
            draft.node.answers != null && Array.isArray(draft.node.answers)
              ? // @ts-expect-error - TS2339 - Property 'answers' does not exist on type 'EditableNode | WritableDraft<object & BaseNode & { type: "condition"; condition_ruleset: LogicalAndExpression; }> | ... 12 more ... | WritableDraft<...>'. | TS7031 - Binding element 'id' implicitly has an 'any' type.
                draft.node.answers.filter(({id}) => id !== action.payload)
              : null;
        }
      });
      return nextState;
    }
  }
  throw new Error('invalid action type');
}

export const writebackDataTypeToResponseType: Partial<
  Record<WritebackDataType, ResponseType>
> = {
  string: 'string',
  number: 'number',
  integer: 'number',
  double: 'number',
  date: 'date',
  phone: 'phone number',
  email: 'email',
  currency: 'money',
  datetime: 'date',
  bigdecimal: 'number',
};

const booleanAnswers = [
  {
    id: 1,
    value: true,
    label: 'Yes',
  },
  {
    id: 2,
    value: false,
    label: 'No',
  },
];

export const multipleChoiceErrorMessage =
  'Sorry, the option you chose is invalid. Please select a valid option.';
export const stringErrorMessage = 'Sorry, I didn\'t understand that.';
export const addressErrorMessage =
  'I\'m sorry, I couldn\'t validate the given address. Can you re-enter? (Please include city, state and zip)';
